import React, {
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react'
import { NavLink } from 'react-router-dom'
import { RiCloseLine, RiArrowDownSFill } from '@remixicon/react'
import { PanelLeft } from 'lucide-react'
import { cx, focusRing } from '../../lib/utils'

// Sidebar Context
type SidebarContextType = {
  isOpen: boolean
  openMobile: boolean
  toggleSidebar: () => void
  closeSidebar: () => void
  setOpenMobile: (open: boolean) => void
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

// Sidebar Provider
interface SidebarProviderProps {
  children: React.ReactNode
}

function SidebarProvider({ children }: SidebarProviderProps) {
  const [isOpen, setIsOpen] = useState(true)
  const [openMobile, setOpenMobile] = useState(false)

  const toggleSidebar = useCallback(() => {
    if (window.innerWidth < 768) {
      setOpenMobile((prev) => !prev)
    } else {
      setIsOpen((prev) => !prev)
    }
  }, [])

  const closeSidebar = useCallback(() => {
    setOpenMobile(false)
  }, [])

  return (
    <SidebarContext.Provider value={{ isOpen, openMobile, toggleSidebar, closeSidebar, setOpenMobile }}>
      <div className="flex min-h-svh w-full" style={{ '--sidebar-width': '16rem' } as React.CSSProperties}>
        {children}
      </div>
    </SidebarContext.Provider>
  )
}

// Sidebar component
interface SidebarProps extends React.ComponentPropsWithoutRef<'div'> {
  children: React.ReactNode
}

function Sidebar({ children, className, ...props }: SidebarProps) {
  const { isOpen, openMobile, closeSidebar } = useSidebar()

  return (
    <>
      {/* Mobile drawer */}
      {openMobile && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={closeSidebar}
            aria-hidden="true"
          />
          <div className="fixed inset-y-0 left-0 z-50 flex w-[var(--sidebar-width)] flex-col bg-gray-100 dark:bg-gray-950 md:hidden">
            <button
              onClick={closeSidebar}
              className="absolute right-4 top-4 rounded-md p-1.5 text-gray-700 hover:bg-gray-200/50 dark:text-gray-300 dark:hover:bg-gray-900"
            >
              <RiCloseLine className="size-5" aria-hidden="true" />
            </button>
            {children}
          </div>
        </>
      )}

      {/* Desktop sidebar */}
      <div className="group peer hidden md:block" data-state={isOpen ? 'expanded' : 'collapsed'}>
        {/* Gap placeholder */}
        <div
          className={cx(
            'relative h-svh w-[var(--sidebar-width)] bg-transparent transition-[width] duration-150 ease-in-out',
            !isOpen && 'w-0',
          )}
        />
        {/* Actual sidebar */}
        <div
          className={cx(
            'fixed inset-y-0 left-0 z-10 hidden h-svh w-[var(--sidebar-width)] flex-col border-r border-gray-200 transition-[left] duration-150 ease-in-out md:flex dark:border-gray-800',
            !isOpen && 'left-[calc(var(--sidebar-width)*-1)]',
            className,
          )}
          {...props}
        >
          <div className="flex h-full w-full flex-col border-r border-gray-300 bg-gray-100 dark:border-gray-800 dark:bg-gray-950">
            {children}
          </div>
        </div>
      </div>
    </>
  )
}

// Sidebar Header
function SidebarHeader({ children, className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div className={cx('flex flex-col gap-2 px-3 py-4', className)} {...props}>
      {children}
    </div>
  )
}

// Sidebar Content
function SidebarContent({ children, className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div className={cx('flex min-h-0 flex-1 flex-col gap-2 overflow-auto', className)} {...props}>
      {children}
    </div>
  )
}

// Sidebar Footer
function SidebarFooter({ children, className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div className={cx('flex flex-col gap-2 p-3', className)} {...props}>
      {children}
    </div>
  )
}

// Sidebar Group
function SidebarGroup({ children, className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div className={cx('relative flex w-full min-w-0 flex-col p-3', className)} {...props}>
      {children}
    </div>
  )
}

// Sidebar Group Content
function SidebarGroupContent({ children, className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div className={cx('w-full text-sm', className)} {...props}>
      {children}
    </div>
  )
}

// Sidebar Menu
function SidebarMenu({ children, className, ...props }: React.ComponentPropsWithoutRef<'ul'>) {
  return (
    <ul className={cx('flex w-full min-w-0 flex-col gap-1', className)} {...props}>
      {children}
    </ul>
  )
}

// Sidebar Menu Item
function SidebarMenuItem({ children, ...props }: React.ComponentPropsWithoutRef<'li'>) {
  return <li {...props}>{children}</li>
}

// Sidebar Link - for main nav items
interface SidebarLinkProps {
  href: string
  icon?: React.ElementType
  isActive?: boolean
  notifications?: number | boolean
  children: React.ReactNode
}

function SidebarLink({ href, icon: Icon, isActive, notifications, children }: SidebarLinkProps) {
  const { closeSidebar } = useSidebar()

  return (
    <NavLink
      to={href}
      onClick={() => {
        if (window.innerWidth < 768) closeSidebar()
      }}
      className={({ isActive: navActive }) =>
        cx(
          'flex items-center justify-between rounded-lg px-2.5 py-2 text-sm font-medium transition',
          // Where you are is filled, not merely tinted: colour alone on text
          // is easy to miss, and it is the one thing a sidebar must answer.
          isActive || navActive
            ? 'bg-white font-medium text-primary-600 shadow-sm dark:bg-gray-900 dark:text-gray-50'
            : 'text-gray-700 hover:bg-gray-200/60 hover:text-gray-950 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-gray-50',
          focusRing,
        )
      }
    >
      <span className="flex items-center gap-x-2.5">
        {Icon && <Icon className="size-[18px] shrink-0" aria-hidden="true" />}
        {children}
      </span>
      {notifications && (
        <span className="inline-flex size-5 items-center justify-center rounded bg-primary-100 text-xs font-medium text-primary-600 dark:bg-primary-500/10 dark:text-primary-500">
          {notifications}
        </span>
      )}
    </NavLink>
  )
}

// Sidebar Collapsible Menu
interface SidebarCollapsibleProps {
  name: string
  icon?: React.ElementType
  defaultOpen?: boolean
  children: React.ReactNode
}

function SidebarCollapsible({ name, icon: Icon, defaultOpen = false, children }: SidebarCollapsibleProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cx(
          'flex w-full items-center justify-between gap-x-2.5 rounded-md p-2 text-sm text-gray-900 transition hover:bg-gray-200/50 dark:text-gray-400 dark:hover:bg-gray-900 dark:hover:text-gray-50',
          focusRing,
        )}
      >
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="size-[18px] shrink-0" aria-hidden="true" />}
          {name}
        </div>
        <RiArrowDownSFill
          className={cx(
            'size-5 shrink-0 transform text-gray-400 transition-transform duration-150 ease-in-out dark:text-gray-600',
            isOpen ? 'rotate-0' : '-rotate-90',
          )}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <ul className="relative mt-1 space-y-1 border-l border-transparent">
          <div className="absolute inset-y-0 left-4 w-px bg-gray-300 dark:bg-gray-800" />
          {children}
        </ul>
      )}
    </div>
  )
}

// Sidebar Sub Link - for nested items
interface SidebarSubLinkProps {
  href: string
  isActive?: boolean
  end?: boolean
  children: React.ReactNode
}

function SidebarSubLink({ href, isActive, end, children }: SidebarSubLinkProps) {
  const { closeSidebar } = useSidebar()

  return (
    <li>
      <NavLink
        to={href}
        end={end}
        onClick={() => {
          if (window.innerWidth < 768) closeSidebar()
        }}
        className={({ isActive: navActive }) =>
          cx(
            'relative flex gap-2 rounded-md py-1.5 pl-9 pr-3 text-sm transition',
            'text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-50',
            (isActive || navActive) &&
              'rounded bg-white text-primary-600 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:text-primary-500 dark:ring-gray-800',
            focusRing,
          )
        }
      >
        {({ isActive: navActive }) => (
          <>
            {(isActive || navActive) && (
              <div
                className="absolute left-4 top-1/2 h-5 w-px -translate-y-1/2 bg-primary-500"
                aria-hidden="true"
              />
            )}
            {children}
          </>
        )}
      </NavLink>
    </li>
  )
}

// Sidebar Trigger (for mobile)
function SidebarTrigger({ className, ...props }: React.ComponentPropsWithoutRef<'button'>) {
  const { toggleSidebar } = useSidebar()

  return (
    <button
      onClick={toggleSidebar}
      className={cx(
        'inline-flex rounded-md p-1.5 hover:bg-gray-200/50 dark:hover:bg-gray-900',
        focusRing,
        className,
      )}
      {...props}
    >
      <PanelLeft className="size-[18px] shrink-0 text-gray-700 dark:text-gray-300" aria-hidden="true" />
      <span className="sr-only">Toggle Sidebar</span>
    </button>
  )
}

// Main Content
function MainContent({ children, className, ...props }: React.ComponentPropsWithoutRef<'main'>) {
  return (
    <main
      className={cx(
        // `min-w-0` is load-bearing: a flex child defaults to `min-width:auto`
        // and refuses to shrink below its content, so on a phone anything
        // intrinsically wide — a tab strip, a table, a long word — pushed the
        // whole shell sideways and took the header's controls off screen with
        // it. Shrinking here is what lets the scroll containers inside do
        // their job instead.
        'min-w-0 flex-1 min-h-screen bg-gray-50 dark:bg-gray-950',
        className,
      )}
      {...props}
    >
      {children}
    </main>
  )
}

// Divider
function Divider({ className, children, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      className={cx(
        'mx-auto my-6 flex w-full items-center justify-between gap-3 text-sm text-gray-500',
        className,
      )}
      {...props}
    >
      {children ? (
        <>
          <div className="h-px w-full bg-gray-200 dark:bg-gray-800" />
          <div className="whitespace-nowrap text-inherit">{children}</div>
          <div className="h-px w-full bg-gray-200 dark:bg-gray-800" />
        </>
      ) : (
        <div className="h-px w-full bg-gray-200 dark:bg-gray-800" />
      )}
    </div>
  )
}

export {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarLink,
  SidebarCollapsible,
  SidebarSubLink,
  SidebarTrigger,
  MainContent,
  Divider,
  useSidebar,
}
